import { defaultAddStatus, defaultAddOwnership } from "../utils/settings.js";
import { getDB } from "./schema.js";
import { pinInheritedSeen } from "../utils/seasons.js";
import { todayDate, nowIso } from "../utils/dates.js";
import { AWARDS, TIMES_FINISHED } from "../components/status-badge.js";
import { queueCloudWrite } from "./sync.js";
import { logItemChange } from "./history.js";

const STORE = "tv";
const CATALOG_STORE = "tv_catalog";

export const NOTE_MAX_LENGTH = 280;

function uuid() {
	if (globalThis.crypto?.randomUUID) return crypto.randomUUID();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

// Override-map key for an episode. Seasons are keyed by their number as a string.
export function epKey(season, number) {
	return `${season}x${number}`;
}

export async function listTvSeries() {
	const db = await getDB();
	return db.getAll(STORE);
}

export async function getTvSeriesById(id) {
	const db = await getDB();
	return db.get(STORE, id);
}

export async function getByTvmazeId(tvmazeId) {
	const db = await getDB();
	return db.getFromIndex(STORE, "tvmaze_id", tvmazeId);
}

export async function addTvSeries({
	tvmaze_id,
	title,
	premiered_year = null,
	cover_url = null,
	summary = null,
	engagement_status = defaultAddStatus(),
	ownership_status = defaultAddOwnership(),
}) {
	const existing = await getByTvmazeId(tvmaze_id);
	if (existing) return existing;

	const now = nowIso();
	const today = todayDate();
	const record = {
		id: uuid(),
		tvmaze_id,
		title,
		premiered_year,
		cover_url,
		summary,
		media_type: "tv",
		engagement_status,
		ownership_status,
		formats: [],
		rating: null,
		awards: [],
		note: null,
		times_finished: null,
		// Per-season / per-episode user opinions. Empty = inherit from the series
		// (season inherits from series; episode inherits from season then series).
		season_overrides: {},
		episode_overrides: {},
		// Canonical column the app no longer uses (kept 0 so the cloud row is valid).
		playthrough_count: 0,
		date_first_completed: engagement_status === "done" ? today : null,
		last_engaged_date:
			engagement_status === "playing" ||
			engagement_status === "replaying" ||
			engagement_status === "done"
				? today
				: null,
		visibility: "friends",
		created_at: now,
		updated_at: now,
	};
	const db = await getDB();
	await db.add(STORE, record);
	await queueCloudWrite({ type: "create", mediaType: "tv", record });
	await logItemChange("tv", null, record);
	return record;
}

export async function updateTvSeries(id, patch) {
	const db = await getDB();
	const current = await db.get(STORE, id);
	if (!current) throw new Error(`TV series ${id} not found`);

	const next = { ...current, ...patch, updated_at: nowIso() };

	if (
		patch.engagement_status &&
		patch.engagement_status !== current.engagement_status
	) {
		const today = todayDate();
		if (
			patch.engagement_status === "playing" ||
			patch.engagement_status === "replaying"
		) {
			next.last_engaged_date = today;
		}
		if (patch.engagement_status === "done") {
			next.last_engaged_date = today;
			if (!current.date_first_completed) next.date_first_completed = today;
		}
		// Leaving "Watched": pin the inherited seen-state onto every released season
		// so what you watched survives the status change (utils/seasons.js).
		if (current.engagement_status === "done" && patch.season_overrides === undefined) {
			const catalog = await getCatalog(current.tvmaze_id);
			if (catalog) {
				next.season_overrides = pinInheritedSeen(current.season_overrides, catalog, today);
			}
		}
	}

	await db.put(STORE, next);
	await queueCloudWrite({ type: "update", mediaType: "tv", record: next });
	await logItemChange("tv", current, next);
	return next;
}

export async function setSeriesRating(id, rating) {
	if (rating !== null && (!Number.isInteger(rating) || rating < 1 || rating > 5)) {
		throw new Error("Rating must be 1-5 or null");
	}
	return updateTvSeries(id, { rating });
}

export async function setSeriesAwards(id, list) {
	if (!Array.isArray(list)) throw new Error("awards must be an array");
	const valid = new Set(AWARDS.map((a) => a.value));
	return updateTvSeries(id, { awards: list.filter((v) => valid.has(v)) });
}

export async function setSeriesNote(id, note) {
	const trimmed = (note ?? "").trim().slice(0, NOTE_MAX_LENGTH);
	return updateTvSeries(id, { note: trimmed === "" ? null : trimmed });
}

export async function setSeriesTimesFinished(id, value) {
	if (value !== null && !TIMES_FINISHED.some((b) => b.value === value)) {
		throw new Error(`Invalid times_finished bucket: ${value}`);
	}
	return updateTvSeries(id, { times_finished: value });
}

// Set/clear one field of one override entry, pruning empty entries so the maps
// stay minimal (an absent entry = "inherit").
function withOverride(map, key, field, value) {
	const next = { ...(map ?? {}) };
	const entry = { ...(next[key] ?? {}) };
	if (value === null || value === undefined) delete entry[field];
	else entry[field] = value;
	if (Object.keys(entry).length === 0) delete next[key];
	else next[key] = entry;
	return next;
}

export async function setSeasonRating(id, seasonNum, rating) {
	if (rating !== null && (!Number.isInteger(rating) || rating < 1 || rating > 5)) {
		throw new Error("Rating must be 1-5 or null");
	}
	const series = await getTvSeriesById(id);
	if (!series) throw new Error(`TV series ${id} not found`);
	let map = withOverride(series.season_overrides, String(seasonNum), "rating", rating);
	// A rating implies you've seen it — clear any explicit "not seen" flag so the
	// rating isn't hidden by the unseen ⇒ 0 stars rule below.
	if (rating != null) map = withOverride(map, String(seasonNum), "seen", true);
	return updateTvSeries(id, { season_overrides: map });
}

export async function setSeasonSeen(id, seasonNum, seen) {
	const series = await getTvSeriesById(id);
	if (!series) throw new Error(`TV series ${id} not found`);
	// Store the explicit boolean so a season can be marked *not* seen even when
	// the whole series is Watched. (null would mean "inherit".)
	let map = withOverride(series.season_overrides, String(seasonNum), "seen", !!seen);
	// Unseeing drops the rating to 0 (you can't rate what you haven't seen).
	if (!seen) map = withOverride(map, String(seasonNum), "rating", null);
	return updateTvSeries(id, { season_overrides: map });
}

export async function setEpisodeRating(id, season, number, rating) {
	if (rating !== null && (!Number.isInteger(rating) || rating < 1 || rating > 5)) {
		throw new Error("Rating must be 1-5 or null");
	}
	const series = await getTvSeriesById(id);
	if (!series) throw new Error(`TV series ${id} not found`);
	let map = withOverride(
		series.episode_overrides,
		epKey(season, number),
		"rating",
		rating,
	);
	// A rating implies you've seen it (see setSeasonRating).
	if (rating != null) map = withOverride(map, epKey(season, number), "seen", true);
	return updateTvSeries(id, { episode_overrides: map });
}

export async function setEpisodeSeen(id, season, number, seen) {
	const series = await getTvSeriesById(id);
	if (!series) throw new Error(`TV series ${id} not found`);
	let map = withOverride(
		series.episode_overrides,
		epKey(season, number),
		"seen",
		!!seen,
	);
	// Unseeing drops the rating to 0 (see setSeasonSeen).
	if (!seen) map = withOverride(map, epKey(season, number), "rating", null);
	return updateTvSeries(id, { episode_overrides: map });
}

export async function deleteTvSeries(id) {
	const db = await getDB();
	const current = await db.get(STORE, id);
	await db.delete(STORE, id);
	if (current) {
		// Catalog is local-only cache; drop it too (re-fetched if re-added).
		await db.delete(CATALOG_STORE, current.tvmaze_id).catch(() => {});
		await queueCloudWrite({ type: "delete", mediaType: "tv", record: current });
		await logItemChange("tv", current, null);
	}
}

export async function deleteByTvmazeId(tvmazeId) {
	const existing = await getByTvmazeId(tvmazeId);
	if (!existing) return false;
	await deleteTvSeries(existing.id);
	return true;
}

// --- inheritance resolvers (pure) ---
//
// Effective value = the most specific override present, else inherited:
//   episode → season → series.

// `released` (default true) gates the INHERITED default only: an unaired season
// or episode doesn't inherit the series rating / "watched" seen-state — but an
// explicit season/episode override always wins, so you can still rate or mark a
// future season by hand.
export function effectiveRating(
	series,
	seasonNum = null,
	epNumber = null,
	{ released = true } = {},
) {
	if (seasonNum != null && epNumber != null) {
		const eo = series.episode_overrides?.[epKey(seasonNum, epNumber)];
		// Explicitly marked not-seen ⇒ 0 stars (no rating), even if it would
		// otherwise inherit the season/series rating.
		if (eo?.seen === false) return null;
		if (eo && eo.rating != null) return eo.rating;
	}
	if (seasonNum != null) {
		const so = series.season_overrides?.[String(seasonNum)];
		if (so?.seen === false) return null;
		if (so && so.rating != null) return so.rating;
	}
	if (!released) return null;
	return series.rating ?? null;
}

export function effectiveSeen(
	series,
	seasonNum = null,
	epNumber = null,
	{ released = true } = {},
) {
	if (seasonNum != null && epNumber != null) {
		const eo = series.episode_overrides?.[epKey(seasonNum, epNumber)];
		if (eo && eo.seen != null) return eo.seen;
	}
	if (seasonNum != null) {
		const so = series.season_overrides?.[String(seasonNum)];
		if (so && so.seen != null) return so.seen;
	}
	if (!released) return false;
	// Default: seen iff the whole series is marked Watched.
	return series.engagement_status === "done";
}

// True when this level (or any more-specific override) carries its own rating,
// so the UI can show an "overridden" affordance vs. the inherited default.
export function hasOwnRating(series, seasonNum = null, epNumber = null) {
	if (seasonNum != null && epNumber != null) {
		return series.episode_overrides?.[epKey(seasonNum, epNumber)]?.rating != null;
	}
	if (seasonNum != null) {
		return series.season_overrides?.[String(seasonNum)]?.rating != null;
	}
	return series.rating != null;
}

// --- catalog cache (local-only, re-fetchable; never synced) ---

export async function getCatalog(tvmazeId) {
	const db = await getDB();
	return db.get(CATALOG_STORE, tvmazeId);
}

// Local-only write — deliberately does NOT queue a cloud op (catalog is
// re-fetchable "available" metadata, not user data).
export async function saveCatalog({ tvmaze_id, seasons, episodes, status = null }) {
	const db = await getDB();
	const record = {
		tvmaze_id,
		seasons: seasons ?? [],
		episodes: episodes ?? [],
		// TVmaze show status ("Ended" → re-checked far less often; utils/seasons.js).
		status,
		fetched_at: nowIso(),
	};
	await db.put(CATALOG_STORE, record);
	return record;
}
